// Segment computed-label layer (DL-029 / ISP-4018).
//
// WHY: unnamed Segments are shown as "Segment 1", "Segment 2", ... but today
// that number is BAKED into the stored `title` at creation time
// (constants/wheelData.js `newSegment` -> `Segment ${id}` and the Drawchart add
// path -> `Segment ${index}`). Because the number is frozen into the data, when
// a user REARRANGES segments the stored numbers no longer match the visual
// order ("Segment 3, Segment 1, Segment 2"). Gordon 2026-07-11:
//   "when rearranged the Segments should still remain in numerical order".
//
// FIX (interim, flag-gated, no data migration): compute the placeholder label
// at RENDER time from the CURRENT wheel order, never from stored numbers. A
// segment with a real user-given name keeps its name; an unnamed segment is
// labelled "Segment N" where N is its 1-based POSITION on the wheel (named
// segments keep their slot, so gaps are allowed). So a wheel of
// [unnamed, unnamed, "Apple", unnamed] renders
// "Segment 1, Segment 2, Apple, Segment 4", and any reorder recomputes.
//
// T22.274 (Gordon 2026-10-14, D1=A, supersedes DL-029 no-gap numbering): the
// old rule counted UNNAMED segments only, so typing the first letter of a name
// into one segment relabelled every unnamed segment after it ("Segment 2..7"
// became "Segment 1..6" on the first keystroke). An automatic label must never
// change when ANOTHER segment is named, so the number is the wheel position.
//
// The follow-up BE change (title nullable + one-time backfill that clears baked
// placeholders) is a SEPARATE, dev-reviewed unit; this layer is pure frontend.

// The exact shape the legacy factories bake into an unnamed segment's title.
// INTERIM LIMITATION (documented honestly): a user who literally types
// "Segment 5" is indistinguishable from baked placeholder data, so this layer
// treats such a title as unnamed and renumbers it. That is exactly the fix
// Gordon asked for on the current (baked) data; the future BE migration removes
// the ambiguity by storing an empty title for genuinely-unnamed segments.
export const PLACEHOLDER_TITLE_PATTERN = /^Segment \d+$/;

// Read the human title off a segment regardless of shape. Wheel chartData items
// carry a top-level `title`; redux/API segments carry `settings.title`. Prefer
// the explicit settings.title, then the top-level title, else "".
export function getSegmentTitle(segment) {
  if (!segment) return "";
  const settingsTitle = segment.settings && segment.settings.title;
  if (settingsTitle != null && settingsTitle !== "") {
    return String(settingsTitle);
  }
  if (segment.title != null) return String(segment.title);
  return "";
}

// A title is "unnamed" when it is empty/whitespace OR is exactly a baked
// "Segment N" placeholder.
export function isUnnamedSegmentTitle(title) {
  const t = title == null ? "" : String(title).trim();
  if (t === "") return true;
  return PLACEHOLDER_TITLE_PATTERN.test(t);
}

export function isUnnamedSegment(segment) {
  return isUnnamedSegmentTitle(getSegmentTitle(segment));
}

// Match two segment records: same object reference, else same non-null id.
function sameSegment(a, b) {
  if (a === b) return true;
  if (a && b && a.id != null && b.id != null) return a.id === b.id;
  return false;
}

// THE shared label function. Given a segment and the full segment list IN WHEEL
// ORDER, return the string to display:
//   - real name    -> the stored name, verbatim
//   - unnamed      -> "Segment N", N = 1-based position of the segment in the
//                     given order (named segments keep their slot, gaps
//                     allowed), so naming one segment never relabels another.
// Pure: no feature-flag check, no I/O. Callers gate on the flag and pass the
// list already in wheel order (use orderSegmentsByPosition when unsure).
export function computeSegmentLabel(segment, allSegmentsInWheelOrder) {
  const title = getSegmentTitle(segment);
  if (!isUnnamedSegmentTitle(title)) return title;

  const list = Array.isArray(allSegmentsInWheelOrder)
    ? allSegmentsInWheelOrder
    : [];

  const index = list.findIndex((s) => sameSegment(s, segment));
  if (index !== -1) return `Segment ${index + 1}`;
  // Defensive: the segment was not found in the list (or the list was empty).
  // Never render a blank label — count it as the next slot after the list.
  return `Segment ${list.length + 1}`;
}

// Stable-sort a copy of the segments by their wheel `position` (the field that
// places wedges around the wheel). Segments without a numeric position keep
// their input order. Never mutates the input.
export function orderSegmentsByPosition(segments) {
  const list = Array.isArray(segments) ? segments : [];
  return list
    .map((seg, i) => ({ seg, i }))
    .sort((a, b) => {
      const pa =
        a.seg && typeof a.seg.position === "number" ? a.seg.position : a.i;
      const pb =
        b.seg && typeof b.seg.position === "number" ? b.seg.position : b.i;
      if (pa !== pb) return pa - pb;
      return a.i - b.i;
    })
    .map((x) => x.seg);
}

// Convenience for the WHEEL render feed (chartData with a top-level `title`).
// Returns a NEW array in the SAME order as the input (createPie renders in array
// order via d3.pie().sort(null)); only the `title` string is replaced with the
// computed label. Numbering is derived from wheel position so a reorder always
// renumbers. Named items are returned by reference (referential stability for
// React memo / createPie diffing); only relabelled items are shallow-cloned.
//
// NB: does NOT check the feature flag — the call site gates with
// SEGMENT_COMPUTED_LABELS_ENABLED and passes the original array through
// untouched when OFF, so flag-OFF is byte-identical.
export function withComputedChartLabels(chartData) {
  if (!Array.isArray(chartData) || chartData.length === 0) return chartData;
  const ordered = orderSegmentsByPosition(chartData);
  return chartData.map((item) => {
    const label = computeSegmentLabel(item, ordered);
    if (item && item.title === label) return item;
    return { ...item, title: label };
  });
}

// WRITE-BACK GUARD. The wheel renderer (createPie) is fed the computed-label
// array above; if its internal drag-reorder writes that array back to state, the
// computed placeholder ("Segment 1") could otherwise be persisted, defeating
// "computed at render, never stored". This restores each item's ORIGINAL stored
// top-level `title` (looked up by id from `sourceById`) while keeping every
// other field (notably the new `position`) from the written-back array. Items
// whose title was never relabelled, or that have no source match, are returned
// unchanged. Never mutates its inputs.
export function restoreOriginalTitles(writtenBack, sourceSegments) {
  if (!Array.isArray(writtenBack)) return writtenBack;
  const source = Array.isArray(sourceSegments) ? sourceSegments : [];
  const titleById = new Map();
  for (const s of source) {
    if (s && s.id != null) titleById.set(s.id, s.title);
  }
  return writtenBack.map((item) => {
    if (!item || item.id == null || !titleById.has(item.id)) return item;
    const original = titleById.get(item.id);
    if (item.title === original) return item;
    return { ...item, title: original };
  });
}
